import { useCallback, useEffect, useState } from 'react';
import {
  useUpdateCorporateApplicationMutation,
  useUpdateCorporateReviewStatusMutation,
  useUpdateNewApplicationMutation,
  useUpdateReviewStatusMutation,
} from '../../store/services';
import {
  ApplicationType,
  Match,
  NewApplicationCorporateInfo,
  NewApplicationListItem,
  ReviewStatus,
} from '../../types';

const useApplicationMatches = ({
  matches,
  applicationType,
  applicationId,
  reviewDuplicatesComment,
}: {
  matches?: {
    node: Match;
  }[];
  applicationType: ApplicationType;
  applicationId?: string;
  reviewDuplicatesComment?: string;
}) => {
  const [comment, setComment] = useState('');
  const [selectedApplicationsArray, setSelectedApplicationsArray] = useState<
    string[]
  >([]);

  const [skipMatches, { isLoading, isSuccess }] =
    useUpdateNewApplicationMutation();
  const [
    skipCorporateMatches,
    { isLoading: corporateIsProgress, isSuccess: corporateIsSuccess },
  ] = useUpdateCorporateApplicationMutation();
  const [updateReviewStatus, { isLoading: reviewStatusUpdate }] =
    useUpdateReviewStatusMutation();
  const [
    updateCorporateReviewStatus,
    { isLoading: corporateReviewStatusUpdate },
  ] = useUpdateCorporateReviewStatusMutation();

  const skipMatchesAction = useCallback(async () => {
    const nextReviewedDuplicates = matches
      ?.filter((item) => item.node.selected)
      .map((item) => item.node.id);
    const selectedDuplicates = matches?.filter((item) => item.node.selected);

    const body = {
      reviewedDuplicates: nextReviewedDuplicates,
      reviewDuplicatesComment: comment,
    };

    if (
      applicationType === ApplicationType.INDIVIDUAL ||
      applicationType === ApplicationType.COMPANY_OFFICIAL
    ) {
      const response = (await skipMatches({
        body,
        id: applicationId as string,
      })) as { data: { data: NewApplicationListItem } };

      if (response.data && selectedDuplicates?.length === matches?.length) {
        updateReviewStatus({
          body: {
            reviewStatus: ReviewStatus.ACCEPTED,
          },
          id: applicationId as string,
        });
      }
    } else {
      const response = (await skipCorporateMatches({
        body,
        id: applicationId as string,
      })) as { data: { data: NewApplicationCorporateInfo } };

      if (response.data && selectedDuplicates?.length === matches?.length) {
        updateCorporateReviewStatus({
          body: {
            reviewStatus: ReviewStatus.ACCEPTED,
          },
          id: applicationId as string,
        });
      }
    }
  }, [comment, matches]);

  const changeReviewStatus = () => {
    if (
      applicationType === ApplicationType.INDIVIDUAL ||
      applicationType === ApplicationType.COMPANY_OFFICIAL
    ) {
      updateReviewStatus({
        body: {
          reviewStatus: ReviewStatus.ACCEPTED,
        },
        id: applicationId as string,
      });
    } else {
      updateCorporateReviewStatus({
        body: {
          reviewStatus: ReviewStatus.ACCEPTED,
        },
        id: applicationId as string,
      });
    }
  };

  useEffect(() => {
    if (reviewDuplicatesComment) {
      setComment(reviewDuplicatesComment);
    }
  }, [reviewDuplicatesComment]);

  useEffect(() => {
    if (isSuccess || corporateIsSuccess) {
      setSelectedApplicationsArray([]);
    }
  }, [isSuccess, corporateIsSuccess]);

  return {
    selectedApplicationsArray,
    setSelectedApplicationsArray,
    comment,
    setComment,
    isLoading:
      isLoading ||
      reviewStatusUpdate ||
      corporateIsProgress ||
      corporateReviewStatusUpdate,
    changeReviewStatus,
    skipMatchesAction,
  };
};

export default useApplicationMatches;
